import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ArrowUpDown, CalendarDays, Layers, Loader2, Package, RefreshCw, Truck } from "lucide-react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Layout } from "@/components/Layout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { isExamShipmentDistributionEnabled } from "@/features/examShipmentDistributionFeature";
import {
  clientsApi,
  examsComposition,
  ExamShipmentEligibleOrder,
  ExamShipmentRedistributionResult,
  ExamsSkuComposition,
  scheduledOrdersApi,
} from "@/lib/api";

interface ShipmentBlock {
  block: number;
  deadlines: string[];
  rows: ExamsSkuComposition[];
  seriesCount: number;
  digitalCount: number;
  printedCount: number;
}

// orderDeadline chega como "AAAA-MM-DD"; formata sem passar por Date para não deslocar o dia por fuso horário.
const formatDeadline = (value: string) => {
  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const todayKey = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const isOpenDeadline = (deadline: string) => deadline.slice(0, 10) >= todayKey();

type ChangedOrder = ExamShipmentRedistributionResult["changedOrders"][number];
type ChangedOrderSortKey = "orderNumber" | "clientName" | "distributedItems" | "shipments" | "extraOrders" | "deadline";

// Primeiro prazo das remessas do pedido ("AAAA-MM-DD" ordena como texto); pedidos sem remessa vão para o fim.
const firstDeadline = (order: ChangedOrder) => order.shipments?.[0]?.deadline?.slice(0, 10) ?? "9999-12-31";

const compareChangedOrders = (a: ChangedOrder, b: ChangedOrder, key: ChangedOrderSortKey) => {
  switch (key) {
    case "orderNumber": return a.orderNumber.localeCompare(b.orderNumber, "pt-BR");
    case "clientName": return a.clientName.localeCompare(b.clientName, "pt-BR", { sensitivity: "base" });
    case "distributedItems": return a.distributedItems - b.distributedItems;
    case "shipments": return (a.shipments?.length ?? 0) - (b.shipments?.length ?? 0);
    case "extraOrders": return a.extraOrders - b.extraOrders;
    case "deadline": return firstDeadline(a).localeCompare(firstDeadline(b));
  }
};

const plural = (count: number, singular: string, pluralLabel: string) =>
  `${count} ${count === 1 ? singular : pluralLabel}`;

export default function ExamShipmentDistribution() {
  const queryClient = useQueryClient();
  const [cycleYear, setCycleYear] = useState<number | null>(null);
  const [deadlineStatusFilter, setDeadlineStatusFilter] = useState<"all" | "open" | "closed">("all");
  const [changedSort, setChangedSort] = useState<{ key: ChangedOrderSortKey; dir: "asc" | "desc" }>({ key: "deadline", dir: "asc" });
  const [selectedBlock, setSelectedBlock] = useState<ShipmentBlock | null>(null);
  const [confirmRedistribute, setConfirmRedistribute] = useState(false);
  const [lastResult, setLastResult] = useState<ExamShipmentRedistributionResult | null>(null);
  const sortedChangedOrders = useMemo(() => {
    const direction = changedSort.dir === "asc" ? 1 : -1;
    return [...(lastResult?.changedOrders ?? [])].sort((a, b) =>
      direction * compareChangedOrders(a, b, changedSort.key) || a.orderNumber.localeCompare(b.orderNumber, "pt-BR"));
  }, [lastResult, changedSort]);
  const [orderToInclude, setOrderToInclude] = useState<ExamShipmentEligibleOrder | null>(null);

  const { data: commercialCycles = [], isLoading: cyclesLoading } = useQuery({
    queryKey: ["commercial-cycles-order"],
    queryFn: async () => {
      const response = await clientsApi.getCycles();
      return response.cycles || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: isExamShipmentDistributionEnabled,
  });
  const toggleChangedSort = (key: ChangedOrderSortKey) =>
    setChangedSort(current => current.key === key
      ? { key, dir: current.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "asc" });

  const availableCycleYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return commercialCycles
      .filter(cycle => cycle.status === "Open"
        && cycle.year >= 2027
        && (cycle.year === currentYear || cycle.year === currentYear + 1))
      .map(cycle => cycle.year)
      .sort((a, b) => a - b)
      .slice(0, 2);
  }, [commercialCycles]);
  useEffect(() => {
    if (availableCycleYears.length === 0) return;
    if (cycleYear == null || !availableCycleYears.includes(cycleYear)) {
      setCycleYear(availableCycleYears[0]);
    }
  }, [availableCycleYears, cycleYear]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["orders", "all-composition"],
    queryFn: async () => {
      const { data: compositions } = await examsComposition.examsSkuComposition();
      return compositions || [];
    },
    enabled: isExamShipmentDistributionEnabled,
  });

  // Composição sem ciclo (cycleYear nulo) vale para todos os ciclos, como na regra do backend.
  const blocks = useMemo<ShipmentBlock[]>(() => {
    if (cycleYear == null) return [];
    const byBlock = new Map<number, ExamsSkuComposition[]>();
    (data ?? [])
      .filter(item => item.cycleYear == null || item.cycleYear === cycleYear)
      .forEach(item => byBlock.set(item.shipmentBlock, [...(byBlock.get(item.shipmentBlock) ?? []), item]));
    return Array.from(byBlock.entries())
      .map(([block, rows]) => ({
        block,
        rows,
        deadlines: Array.from(new Set(rows.map(row => row.orderDeadline.slice(0, 10)))).sort(),
        seriesCount: new Set(rows.map(row => row.seriesId)).size,
        digitalCount: new Set(rows.map(row => row.digitalSku)).size,
        printedCount: new Set(rows.map(row => row.printedSku)).size,
      }))
      .sort((left, right) => left.block - right.block);
  }, [data, cycleYear]);

  // Distribuição por data: quais remessas e quantos produtos impressos vencem em cada prazo.
  const deadlineDistribution = useMemo(() => {
    const byDeadline = new Map<string, { blocks: Set<number>; printed: Set<number>; digital: Set<number> }>();
    blocks.forEach(block => block.rows.forEach(row => {
      const key = row.orderDeadline.slice(0, 10);
      const current = byDeadline.get(key) ?? { blocks: new Set<number>(), printed: new Set<number>(), digital: new Set<number>() };
      current.blocks.add(block.block);
      current.printed.add(row.printedSku);
      current.digital.add(row.digitalSku);
      byDeadline.set(key, current);
    }));
    return Array.from(byDeadline.entries())
      .map(([deadline, value]) => ({
        deadline,
        blocks: Array.from(value.blocks).sort((a, b) => a - b),
        printedCount: value.printed.size,
        digitalCount: value.digital.size,
      }))
      .sort((left, right) => left.deadline.localeCompare(right.deadline));
  }, [blocks]);

  const deadlineStatusCounts = useMemo(() => {
    const open = deadlineDistribution.filter(item => isOpenDeadline(item.deadline)).length;
    return { all: deadlineDistribution.length, open, closed: deadlineDistribution.length - open };
  }, [deadlineDistribution]);

  const filteredDeadlineDistribution = useMemo(() => deadlineDistribution.filter(item =>
    deadlineStatusFilter === "all" || (deadlineStatusFilter === "open") === isOpenDeadline(item.deadline)),
  [deadlineDistribution, deadlineStatusFilter]);

  const rowsByDeadline = useMemo(() => {
    if (!selectedBlock) return [];
    const grouped = new Map<string, ExamsSkuComposition[]>();
    selectedBlock.rows.forEach(row => {
      const key = row.orderDeadline.slice(0, 10);
      grouped.set(key, [...(grouped.get(key) ?? []), row]);
    });
    return Array.from(grouped.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([deadline, rows]) => ({
        deadline,
        rows: [...rows].sort((a, b) => a.digitalSku - b.digitalSku || a.seriesId - b.seriesId || a.printedSku - b.printedSku),
      }));
  }, [selectedBlock]);

  // Pedidos com remessa vencida que podem ser incluídos individualmente (item pedido dentro do intervalo da remessa).
  const { data: eligibleOrders = [], isLoading: eligibleLoading } = useQuery({
    queryKey: ["exam-shipment-redistribution-eligible", cycleYear],
    queryFn: async () => {
      const response = await scheduledOrdersApi.getRedistributionEligible(cycleYear!);
      return response.data ?? [];
    },
    enabled: isExamShipmentDistributionEnabled && cycleYear != null,
  });

  const redistribute = useMutation({
    mutationFn: async (variables: { year: number; order?: ExamShipmentEligibleOrder }) => {
      const response = await scheduledOrdersApi.redistributeExamShipments(
        variables.year,
        variables.order ? { scheduledOrderId: variables.order.scheduledOrderId, includeExpired: true } : undefined,
      );
      return response.data;
    },
    onSuccess: result => {
      setLastResult(result);
      queryClient.invalidateQueries({ queryKey: ["scheduled-orders"] });
      queryClient.invalidateQueries({ queryKey: ["exam-shipment-redistribution-eligible"] });
      if (result.errors.length > 0) {
        toast.warning(`Redistribuição concluída com ${plural(result.errors.length, "erro", "erros")}. Veja o resumo abaixo.`);
      } else if (result.shipmentOrders === 0 && result.pendingItems === 0) {
        toast.success("Nenhuma redistribuição necessária: os itens já estão nas remessas corretas.");
      } else {
        toast.success("Entregas redistribuídas.");
      }
    },
    onError: (error: unknown) => {
      const message = (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message
        ?? (error as { message?: string })?.message
        ?? "Não foi possível redistribuir as entregas.";
      toast.error(message);
    },
  });

  if (!isExamShipmentDistributionEnabled) {
    return <Navigate to="/admin-pedidos" replace />;
  }

  return (
    <Layout title="Distribuição de Remessas">
      <div className="space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <Truck className="h-5 w-5" />
            <p>
              Remessas (shipment_blocks) de avaliações e simulados por ciclo, com os produtos interligados e os prazos de cada uma.
            </p>
          </div>
          <Button
            onClick={() => setConfirmRedistribute(true)}
            disabled={cycleYear == null || redistribute.isPending}
          >
            {redistribute.isPending
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <RefreshCw className="mr-2 h-4 w-4" />}
            Redistribuir entregas
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {cyclesLoading
            ? <><Skeleton className="h-20" /><Skeleton className="h-20" /></>
            : availableCycleYears.map(year => {
              const isSelected = cycleYear === year;
              return <button key={year} type="button" onClick={() => setCycleYear(year)} disabled={isSelected}
                className={`relative rounded-lg border-2 p-4 text-left transition-colors ${isSelected
                  ? "border-primary bg-primary/5 cursor-default"
                  : "border-gray-300 bg-gray-50 hover:border-primary/50 hover:bg-primary/5 cursor-pointer"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Package className={`h-5 w-5 ${isSelected ? "text-primary" : "text-gray-400"}`} />
                    <span className={`font-semibold ${isSelected ? "text-primary" : "text-gray-500"}`}>Ciclo {year}</span>
                  </div>
                  {isSelected && <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">● Ativo</span>}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{isSelected ? "Remessas exibidas abaixo" : "Clique para trocar para este ciclo"}</p>
              </button>;
            })}
        </div>

        {lastResult && (
          <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="flex items-center justify-between text-base">
                Resultado da última redistribuição (ciclo {lastResult.cycleYear})
                <Button variant="ghost" size="sm" onClick={() => setLastResult(null)}>Fechar</Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 p-4 text-sm">
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{plural(lastResult.ordersAnalyzed, "pedido analisado", "pedidos analisados")}</Badge>
                <Badge variant="outline">{plural(lastResult.ordersChanged, "pedido ajustado", "pedidos ajustados")}</Badge>
                <Badge variant="outline">{plural(lastResult.distributedItems, "item distribuído", "itens distribuídos")}</Badge>
                <Badge variant="outline">{plural(lastResult.shipmentOrders, "remessa criada/atualizada", "remessas criadas/atualizadas")}</Badge>
                <Badge variant="outline">{plural(lastResult.extraOrders, "pedido extra criado", "pedidos extras criados")}</Badge>
                <Badge variant={lastResult.pendingItems > 0 ? "secondary" : "outline"}>
                  {plural(lastResult.pendingItems, "item aguardando composição", "itens aguardando composição")}
                </Badge>
              </div>
              {lastResult.changedOrders.length > 0 && (
                <div>
                  <p className="mb-2 font-medium">Pedidos redistribuídos</p>
                  <Table>
                    <TableHeader><TableRow>
                      {([
                        ["orderNumber", "Pedido"], ["clientName", "Cliente"], ["distributedItems", "Itens distribuídos"],
                        ["shipments", "Remessas"], ["deadline", "Data da remessa"], ["extraOrders", "Extras"],
                      ] as [ChangedOrderSortKey, string][]).map(([key, label]) => {
                        const active = changedSort.key === key;
                        const SortIcon = !active ? ArrowUpDown : changedSort.dir === "asc" ? ArrowUp : ArrowDown;
                        return <TableHead key={key} aria-sort={active ? (changedSort.dir === "asc" ? "ascending" : "descending") : "none"}>
                          <button type="button" onClick={() => toggleChangedSort(key)}
                            className="inline-flex items-center gap-1 font-medium hover:text-foreground">
                            {label}<SortIcon className={`h-3.5 w-3.5 ${active ? "text-primary" : "opacity-50"}`} />
                          </button>
                        </TableHead>;
                      })}
                    </TableRow></TableHeader>
                    <TableBody>{sortedChangedOrders.map(order => <TableRow key={order.scheduledOrderId}>
                      <TableCell className="font-medium">{order.orderNumber}</TableCell>
                      <TableCell>{order.clientName} <span className="text-xs text-muted-foreground">({order.clientCode})</span></TableCell>
                      <TableCell>{order.distributedItems}</TableCell>
                      <TableCell>{order.shipmentOrders}</TableCell>
                      <TableCell>
                        {order.shipments?.length
                          ? <div className="flex flex-wrap gap-1">{order.shipments.map(shipment =>
                            <Badge key={`${shipment.shipmentBlock}-${shipment.deadline}`} variant="outline" className="whitespace-nowrap font-normal">
                              Remessa {shipment.shipmentBlock} · {formatDeadline(shipment.deadline)}
                            </Badge>)}</div>
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>{order.extraOrders}</TableCell>
                    </TableRow>)}</TableBody>
                  </Table>
                </div>
              )}
              {lastResult.errors.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-destructive">
                  {lastResult.errors.map(error => <li key={error}>{error}</li>)}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="shadow-sm">
          <CardHeader className="border-b bg-muted/20">
            <CardTitle className="flex items-center justify-between text-base">
              <span className="flex items-center gap-2"><Layers className="h-5 w-5 text-blue-700" />Remessas{cycleYear ? ` de ${cycleYear}` : ""}</span>
              <Badge variant="outline">{plural(blocks.length, "remessa", "remessas")}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? <div className="space-y-3 p-6"><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
              : isError ? <div className="p-8 text-center text-sm text-destructive">Não foi possível consultar a composição das remessas.</div>
              : blocks.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Nenhuma remessa cadastrada em exams_sku_composition para o ciclo.</div>
              : <Table>
                <TableHeader><TableRow>
                  <TableHead>Remessa</TableHead><TableHead>Prazo</TableHead><TableHead>Séries</TableHead>
                  <TableHead>Produtos digitais</TableHead><TableHead>Produtos impressos</TableHead><TableHead className="text-right">Produtos</TableHead>
                </TableRow></TableHeader>
                <TableBody>{blocks.map(block => <TableRow key={block.block}>
                  <TableCell className="font-medium">Remessa {block.block}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {block.deadlines.map(deadline => <Badge key={deadline} variant={isOpenDeadline(deadline) ? "default" : "secondary"}>
                        {formatDeadline(deadline)}{isOpenDeadline(deadline) ? "" : " · encerrado"}
                      </Badge>)}
                    </div>
                  </TableCell>
                  <TableCell>{block.seriesCount}</TableCell>
                  <TableCell>{block.digitalCount}</TableCell>
                  <TableCell>{block.printedCount}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" onClick={() => setSelectedBlock(block)}>Ver produtos</Button>
                  </TableCell>
                </TableRow>)}</TableBody>
              </Table>}
          </CardContent>
        </Card>

        {(eligibleLoading || eligibleOrders.length > 0) && (
          <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2"><Truck className="h-5 w-5 text-blue-700" />Pedidos fora do prazo elegíveis</span>
                <Badge variant="outline">{plural(eligibleOrders.length, "pedido", "pedidos")}</Badge>
              </CardTitle>
              <p className="text-xs font-normal text-muted-foreground">
                Itens pedidos dentro do intervalo de uma remessa cujo prazo já venceu e que ainda não foram distribuídos nela.
                A redistribuição geral não os inclui; use "Incluir" para distribuir um pedido específico.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              {eligibleLoading ? <div className="space-y-3 p-6"><Skeleton className="h-10" /><Skeleton className="h-10" /></div>
                : <Table>
                  <TableHeader><TableRow>
                    <TableHead>Pedido</TableHead><TableHead>Cliente</TableHead><TableHead>Itens</TableHead>
                    <TableHead>Prazos vencidos</TableHead><TableHead className="text-right">Ação</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{eligibleOrders.map(order => <TableRow key={order.scheduledOrderId}>
                    <TableCell className="font-medium">{order.orderNumber}</TableCell>
                    <TableCell>{order.clientName} <span className="text-xs text-muted-foreground">({order.clientCode})</span></TableCell>
                    <TableCell>{order.items.map(item => <div key={`${item.productSku}-${item.productSeries}`}>
                      {item.productDescription} <span className="text-xs text-muted-foreground">· SKU {item.productSku} · {item.quantity} un.</span>
                    </div>)}</TableCell>
                    <TableCell>{Array.from(new Set(order.items.flatMap(item => item.expiredDeadlines)))
                      .sort().map(deadline => formatDeadline(deadline)).join(", ")}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" disabled={redistribute.isPending} onClick={() => setOrderToInclude(order)}>
                        Incluir
                      </Button>
                    </TableCell>
                  </TableRow>)}</TableBody>
                </Table>}
            </CardContent>
          </Card>
        )}

        {blocks.length > 0 && (
          <Card className="shadow-sm">
            <CardHeader className="border-b bg-muted/20">
              <CardTitle className="flex items-center justify-between text-base">
                <span className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-blue-700" />Distribuição por data</span>
                <Badge variant="outline">{plural(filteredDeadlineDistribution.length, "prazo", "prazos")}</Badge>
              </CardTitle>
              <div className="flex flex-wrap items-center gap-2 pt-3" role="group" aria-label="Filtrar prazos por situação">
                <span className="text-sm text-muted-foreground">Situação:</span>
                {([["all", "Todos"], ["open", "Vigente"], ["closed", "Encerrado"]] as const).map(([value, label]) =>
                  <Button key={value} type="button" size="sm" aria-pressed={deadlineStatusFilter === value}
                    variant={deadlineStatusFilter === value ? "default" : "outline"}
                    onClick={() => setDeadlineStatusFilter(value)}>
                    {label} ({deadlineStatusCounts[value]})
                  </Button>)}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Prazo</TableHead><TableHead>Situação</TableHead><TableHead>Remessas</TableHead>
                  <TableHead>Produtos digitais</TableHead><TableHead>Produtos impressos</TableHead>
                </TableRow></TableHeader>
                <TableBody>{filteredDeadlineDistribution.length === 0
                  ? <TableRow><TableCell colSpan={5} className="py-6 text-center text-muted-foreground">Nenhum prazo com essa situação.</TableCell></TableRow>
                  : filteredDeadlineDistribution.map(item => <TableRow key={item.deadline}>
                  <TableCell className="font-medium">{formatDeadline(item.deadline)}</TableCell>
                  <TableCell><Badge variant={isOpenDeadline(item.deadline) ? "default" : "secondary"}>{isOpenDeadline(item.deadline) ? "Vigente" : "Encerrado"}</Badge></TableCell>
                  <TableCell>{item.blocks.map(block => `Remessa ${block}`).join(", ")}</TableCell>
                  <TableCell>{item.digitalCount}</TableCell>
                  <TableCell>{item.printedCount}</TableCell>
                </TableRow>)}</TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={selectedBlock != null} onOpenChange={open => { if (!open) setSelectedBlock(null); }}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Remessa {selectedBlock?.block} — produtos interligados</DialogTitle>
            <DialogDescription>
              Cada produto digital pedido gera os produtos impressos abaixo no prazo indicado.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            {rowsByDeadline.map(group => <div key={group.deadline} className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <CalendarDays className="h-4 w-4 text-blue-700" />
                Prazo {formatDeadline(group.deadline)}
                <Badge variant={isOpenDeadline(group.deadline) ? "default" : "secondary"}>{isOpenDeadline(group.deadline) ? "Vigente" : "Encerrado"}</Badge>
                <Badge variant="outline">{plural(group.rows.length, "produto", "produtos")}</Badge>
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>SKU digital</TableHead><TableHead>SKU impresso</TableHead>
                  <TableHead>Descrição (ERP)</TableHead><TableHead>Segmento</TableHead><TableHead>Série</TableHead>
                </TableRow></TableHeader>
                <TableBody>{group.rows.map(row => <TableRow key={row.id}>
                  <TableCell>{row.digitalSku}</TableCell>
                  <TableCell>{row.printedSku}</TableCell>
                  <TableCell>{row.erpDescription}</TableCell>
                  <TableCell>{row.segmentName ?? "—"}</TableCell>
                  <TableCell>{row.seriesName ?? row.seriesId}</TableCell>
                </TableRow>)}</TableBody>
              </Table>
            </div>)}
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmRedistribute} onOpenChange={setConfirmRedistribute}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Redistribuir entregas do ciclo {cycleYear}?</AlertDialogTitle>
            <AlertDialogDescription>
              Os pedidos programados já criados serão analisados e as avaliações e simulados serão colocados nas remessas
              corretas conforme a composição acima. Quando a remessa de um prazo já estiver confirmada ou congelada, será
              criado um pedido extra apenas com a quantidade que falta. Pedidos já distribuídos não são duplicados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (cycleYear != null) redistribute.mutate({ year: cycleYear });
              }}
            >
              Redistribuir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={orderToInclude != null} onOpenChange={open => { if (!open) setOrderToInclude(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Incluir o pedido {orderToInclude?.orderNumber}?</AlertDialogTitle>
            <AlertDialogDescription>
              Os itens deste pedido serão distribuídos também nas remessas já vencidas do intervalo em que foram pedidos
              (além das vigentes). Os demais pedidos não são alterados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (cycleYear != null && orderToInclude) redistribute.mutate({ year: cycleYear, order: orderToInclude });
              }}
            >
              Incluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
